import { z } from 'zod';
import { authSessionResponseSchema } from './auth';

/** Quantos códigos de recuperação cada pessoa recebe (uso único). */
export const RECOVERY_CODE_COUNT = 10;

/** Código do app autenticador: 6 números (espaços ignorados, como o app costuma mostrar "123 456"). */
export const totpCodeSchema = z
  .string()
  .transform((v) => v.replace(/\s+/g, ''))
  .pipe(z.string().regex(/^\d{6}$/, 'O código tem 6 números.'));

/** Código de recuperação ("abcde-fghjk"): maiúsculas, espaços e hífen ignorados. */
export const recoveryCodeSchema = z
  .string()
  .transform((v) => v.toLowerCase().replace(/[^a-z0-9]/g, ''))
  .pipe(z.string().length(10, 'O código de recuperação tem 10 letras e números.'));

/** Código do app autenticador OU de recuperação (desativar, gerar novos códigos). */
export const anyTwoFactorCodeSchema = z
  .string()
  .trim()
  .min(6, 'Informe o código do app autenticador ou um código de recuperação.')
  .max(20, 'Código inválido.');

/**
 * Senha certa, falta a segunda etapa: 'required' = pedir o código; 'setup' = a empresa exige 2FA e a
 * pessoa ainda não ativou — ativa agora, antes de receber a sessão.
 */
export const twoFactorChallengeSchema = z.object({
  twoFactor: z.enum(['required', 'setup']),
  challengeToken: z.string(),
  /** Segundos de validade do desafio. */
  expiresIn: z.number().int(),
  /** Conta do desafio (a volta do SSO mostra na ativação do 2FA). */
  email: z.string().optional(),
});
export type TwoFactorChallenge = z.infer<typeof twoFactorChallengeSchema>;

/** Resposta do login: a sessão ou o desafio da verificação em duas etapas. */
export const loginResponseSchema = z.union([authSessionResponseSchema, twoFactorChallengeSchema]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/** Segunda etapa do login: código do app autenticador ou um código de recuperação. */
export const twoFactorLoginSchema = z
  .object({
    challengeToken: z.string().min(20).max(200),
    code: totpCodeSchema.optional(),
    recoveryCode: recoveryCodeSchema.optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: 'Informe o código do app autenticador ou um código de recuperação.',
    path: ['code'],
  });
export type TwoFactorLoginRequest = z.input<typeof twoFactorLoginSchema>;

/** Sessão aberta pela segunda etapa (com quantos códigos de recuperação restam, se um foi usado). */
export const twoFactorSessionResponseSchema = authSessionResponseSchema.extend({
  recoveryCodesRemaining: z.number().int().optional(),
});

/** Situação do 2FA da própria conta. */
export const twoFactorStatusSchema = z.object({
  enabled: z.boolean(),
  /** O perfil da pessoa exige 2FA (não dá para desativar). */
  required: z.boolean(),
  enabledAt: z.string().nullable(),
  recoveryCodesRemaining: z.number().int(),
});
export type TwoFactorStatus = z.infer<typeof twoFactorStatusSchema>;

/** Início da ativação: segredo para o QR code (e para digitar à mão). */
export const twoFactorSetupSchema = z.object({
  secret: z.string(),
  otpauthUri: z.string(),
});
export type TwoFactorSetup = z.infer<typeof twoFactorSetupSchema>;

export const twoFactorEnableSchema = z.object({ code: totpCodeSchema });

/** Códigos de recuperação (mostrados uma única vez). */
export const recoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });

/** Ações sensíveis (desativar, gerar novos códigos): senha e código. */
export const twoFactorConfirmSchema = z.object({
  password: z.string().min(1, 'Informe a senha.').max(128),
  code: anyTwoFactorCodeSchema,
});
export type TwoFactorConfirm = z.input<typeof twoFactorConfirmSchema>;

/** Ativação obrigatória durante o login (a empresa exige 2FA): o desafio da etapa de ativação. */
export const twoFactorLoginSetupSchema = z.object({ challengeToken: z.string().min(20).max(200) });
export const twoFactorLoginActivateSchema = z.object({
  challengeToken: z.string().min(20).max(200),
  code: totpCodeSchema,
});
/** Ativou no login: a sessão e os códigos de recuperação (mostrados uma vez antes de entrar). */
export const twoFactorActivationResponseSchema = twoFactorSessionResponseSchema.extend({
  recoveryCodes: z.array(z.string()),
});

/** Política da empresa: perfis que exigem 2FA. */
export const twoFactorPolicySchema = z.object({
  roles: z.array(
    z.object({
      id: z.string().uuid(),
      key: z.enum(['FLUXUS_ADMIN', 'FLUXUS_CONSULTANT', 'EXECUTIVE', 'MANAGER', 'OPERATION']),
      name: z.string(),
      required: z.boolean(),
    }),
  ),
});
export type TwoFactorPolicy = z.infer<typeof twoFactorPolicySchema>;
export const twoFactorPolicyInputSchema = z.object({
  requiredRoleIds: z
    .array(z.string().uuid('Perfil inválido.'))
    .max(20)
    .transform((ids) => [...new Set(ids)]),
  reason: z.string().trim().max(1000, 'A justificativa pode ter no máximo 1000 caracteres.').optional(),
});
